import { useEffect, useState } from 'react';
import { Card, Loader, Btn } from './UI';
import { addDays } from '../lib/alerts';
import { CATS, CAT_ORDER, currentMonday, weekLabel, loadStaffSummary, exportStaffExcel } from '../lib/staffplan';

function Counts({ rows }) {
  return (
    <span style={{ display: 'inline-flex', gap: 6 }}>
      {CAT_ORDER.map(c => {
        const n = rows.filter(r => r.category === c).length;
        return <span key={c} title={CATS[c].label} style={{ fontSize: 12, fontWeight: 700, padding: '1px 8px', borderRadius: 10,
          background: n ? CATS[c].bg : '#F1F4F9', color: n ? CATS[c].color : '#A0AEC0' }}>{CATS[c].label[0]} {n}</span>;
      })}
    </span>
  );
}

function Group({ title, rows, levels, depth, open, setOpen, path }) {
  const id = path + '/' + title;
  const isOpen = open[id] !== false && (depth < 1 || open[id]);
  if (!levels.length) {
    return (
      <div style={{ marginLeft: depth * 14 }}>
        {CAT_ORDER.flatMap(c => rows.filter(r => r.category === c)).map(r => (
          <div key={r.id} style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '4px 0', fontSize: 13, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, fontWeight: 700, padding: '1px 8px', borderRadius: 6, background: CATS[r.category].bg, color: CATS[r.category].color, minWidth: 78, textAlign: 'center' }}>{CATS[r.category].label}</span>
            <b>{r.route}</b><span style={{ color: 'var(--text-muted)' }}>{r.mrc}</span>
            {r.reason && <span style={{ color: '#B3261E' }}>· {r.reason}</span>}
          </div>))}
      </div>
    );
  }
  const [key, ...rest] = levels;
  const groups = {};
  rows.forEach(r => { (groups[r[key]] = groups[r[key]] || []).push(r); });
  return (
    <div style={{ marginLeft: depth ? 14 : 0 }}>
      {Object.keys(groups).sort((a, b) => a.localeCompare(b, 'ru')).map(g => {
        const gid = path + '/' + g;
        const o = open[gid] !== undefined ? open[gid] : depth < 1;
        return (
          <div key={g} style={{ borderTop: '1px solid #EEF1F7', padding: '6px 0' }}>
            <div onClick={() => setOpen({ ...open, [gid]: !o })} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '4px 0' }}>
              <span style={{ width: 14, color: 'var(--text-muted)' }}>{o ? '▾' : '▸'}</span>
              <b style={{ flex: 1, fontSize: 13 }}>{g}</b>
              <Counts rows={groups[g]} />
            </div>
            {o && <Group title={g} rows={groups[g]} levels={rest} depth={depth + 1} open={open} setOpen={setOpen} path={gid} />}
          </div>
        );
      })}
    </div>
  );
}

// mode: 'tm' (город → СВ) | 'rm' / 'trainer' (BU → ТМ → СВ → город). Только просмотр.
export default function StaffPlanSummary({ mode, tmId }) {
  const [week, setWeek] = useState(currentMonday());
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState({});

  useEffect(() => {
    let off = false;
    setRows(null); setErr('');
    loadStaffSummary(week, mode === 'tm' ? { tmId } : {})
      .then(r => { if (!off) setRows(r); })
      .catch(e => { if (!off) { setErr(e.message || String(e)); setRows([]); } });
    return () => { off = true; };
  }, [week, mode, tmId]);

  const levels = mode === 'tm' ? ['city', 'sv'] : ['bu', 'tm', 'sv', 'city'];
  return (
    <Card>
      <div style={{ padding: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 15, flex: 1, minWidth: 160 }}>Планирование кадров СВ</div>
          <button onClick={() => setWeek(addDays(week, -7))} style={{ border: '1px solid var(--border)', background: '#fff', borderRadius: 8, padding: '6px 12px', cursor: 'pointer' }}>‹</button>
          <span style={{ fontSize: 13, fontWeight: 700, background: '#14213D', color: '#fff', borderRadius: 8, padding: '7px 12px' }}>{weekLabel(week)}</span>
          <button onClick={() => setWeek(addDays(week, 7))} style={{ border: '1px solid var(--border)', background: '#fff', borderRadius: 8, padding: '6px 12px', cursor: 'pointer' }}>›</button>
          <Btn size="sm" onClick={() => rows && exportStaffExcel(rows, week)} disabled={!rows || !rows.length}>⬇ Excel</Btn>
        </div>
        {err && <div style={{ fontSize: 12, color: 'var(--red)', marginBottom: 8 }}>{err}. Если таблицы нет — примените SQL v108.</div>}
        {!rows ? <Loader /> : (
          <>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
              {CAT_ORDER.map(c => (
                <div key={c} style={{ flex: '1 1 110px', background: CATS[c].bg, borderRadius: 10, padding: '8px 12px' }}>
                  <div style={{ fontSize: 11, color: CATS[c].color }}>{CATS[c].label}</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: CATS[c].color }}>{rows.filter(r => r.category === c).length}</div>
                </div>))}
            </div>
            {rows.length ? <Group title="root" rows={rows} levels={levels} depth={0} open={open} setOpen={setOpen} path="" />
              : <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>На эту неделю ничего не запланировано.</div>}
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
              {mode === 'tm' ? 'Город → СВ → маршруты.' : 'BU → ТМ → СВ → город → маршруты.'} А — активация, С — стажировка, О — откол.
            </div>
          </>
        )}
      </div>
    </Card>
  );
}
